import React, { useEffect, useId, useRef, useState } from "react";

function normaliseTime(value) {
  const match = String(value || "").trim().match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return "";
  const hour = Math.min(23, Math.max(0, Number(match[1])));
  const minute = Math.min(59, Math.max(0, Number(match[2])));
  return `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
}

function toDraft(value) {
  const normalised = normaliseTime(value) || "09:00";
  const [hour24, minute] = normalised.split(":").map(Number);
  const period = hour24 >= 12 ? "PM" : "AM";
  const hour12 = hour24 % 12 || 12;
  return { hour: String(hour12).padStart(2, "0"), minute: String(minute).padStart(2, "0"), period };
}

export function formatAfriTime(value) {
  if (!normaliseTime(value)) return "Set time";
  const { hour, minute, period } = toDraft(value);
  return `${Number(hour)}:${minute} ${period}`;
}

export function AfriTimePickerPanel({
  label,
  headline = label,
  value,
  onChange,
  onClose,
  returnFocusRef,
  inline = false,
}) {
  const [draft, setDraft] = useState(() => toDraft(value));
  const [error, setError] = useState("");
  const hourRef = useRef(null);

  useEffect(() => setDraft(toDraft(value)), [value]);
  useEffect(() => {
    window.requestAnimationFrame(() => hourRef.current?.focus());
  }, []);

  const dismiss = () => {
    onClose?.();
    window.requestAnimationFrame(() => returnFocusRef?.current?.focus?.());
  };

  const commit = () => {
    const hour = Number(draft.hour);
    const minute = Number(draft.minute);
    if (!Number.isInteger(hour) || hour < 1 || hour > 12 || !Number.isInteger(minute) || minute < 0 || minute > 59) {
      setError("Enter a valid time.");
      return;
    }
    const hour24 = draft.period === "PM" ? (hour % 12) + 12 : hour % 12;
    onChange?.(`${String(hour24).padStart(2, "0")}:${String(minute).padStart(2, "0")}`);
    setError("");
    dismiss();
  };

  const handleInputKeyDown = (event) => {
    if (event.key === "Enter") { event.preventDefault(); commit(); }
  };

  return (
    <div
      className={`afri-time-picker__panel${inline ? " afri-time-picker__panel--inline" : ""}`}
      role="dialog"
      aria-modal={inline ? undefined : "true"}
      aria-label={`${label} keyboard picker`}
      onKeyDown={(event) => { if (event.key === "Escape") { event.preventDefault(); event.stopPropagation(); dismiss(); } }}
    >
      <p className="afri-time-picker__headline">{headline}</p>
      <div className="afri-time-picker__keyboard-fields">
        <label><input ref={hourRef} aria-label="Hour" inputMode="numeric" maxLength="2" value={draft.hour} onKeyDown={handleInputKeyDown} onChange={(event) => setDraft((current) => ({ ...current, hour: event.target.value.replace(/\D/g, "").slice(0, 2) }))} /><span>Hour</span></label>
        <span className="afri-time-picker__colon" aria-hidden="true">:</span>
        <label><input aria-label="Minute" inputMode="numeric" maxLength="2" value={draft.minute} onKeyDown={handleInputKeyDown} onChange={(event) => setDraft((current) => ({ ...current, minute: event.target.value.replace(/\D/g, "").slice(0, 2) }))} /><span>Minute</span></label>
        <div className="afri-time-picker__period" role="group" aria-label="AM or PM">
          {['AM', 'PM'].map((period) => <button key={period} type="button" className={draft.period === period ? "is-selected" : ""} onClick={() => setDraft((current) => ({ ...current, period }))}>{period}</button>)}
        </div>
      </div>
      {error ? <p className="afri-time-picker__error" role="alert">{error}</p> : null}
      <div className="afri-time-picker__actions">
        <button type="button" className="afri-time-picker__mode-action" aria-label="Focus time entry" onClick={() => hourRef.current?.focus()}>
          <img src="/icons/medical-institution/schedule_Fill.svg" alt="" width="24" height="24" aria-hidden="true" />
        </button>
        <div className="afri-time-picker__trailing-actions">
          <button type="button" onClick={dismiss}>Cancel</button>
          <button type="button" onClick={commit}>OK</button>
        </div>
      </div>
    </div>
  );
}

export default function AfriTimePicker({ label, headline = label, value, onChange, disabled = false, id }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const generatedId = useId();
  const inputId = id || `afri-time-${generatedId}`;

  useEffect(() => {
    if (!open) return undefined;
    const close = (event) => {
      if (!rootRef.current?.contains(event.target)) {
        setOpen(false);
        window.requestAnimationFrame(() => triggerRef.current?.focus());
      }
    };
    document.addEventListener("pointerdown", close);
    return () => document.removeEventListener("pointerdown", close);
  }, [open]);

  return (
    <div className={`afri-time-picker${disabled ? " is-disabled" : ""}`} ref={rootRef}>
      <button ref={triggerRef} id={inputId} type="button" className="afri-time-picker__trigger" onClick={() => setOpen((current) => !current)} disabled={disabled} aria-label={`Choose ${label.toLowerCase()} time`} aria-haspopup="dialog" aria-expanded={open}>
        <span>{formatAfriTime(value)}</span>
      </button>
      {open && !disabled ? <AfriTimePickerPanel label={label} headline={headline} value={value} onChange={onChange} onClose={() => setOpen(false)} returnFocusRef={triggerRef} /> : null}
    </div>
  );
}
